import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check, Zap, Star, Crown, Plus } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const WHATSAPP_URL = 'https://wa.me/917398073200?text=I%20want%20a%20free%20menu%20audit';

export const Pricing = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section 
      id="pricing" 
      ref={sectionRef}
      className="py-20 md:py-32 relative"
      data-testid="pricing-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-gradient-to-br from-[#F59E0B]/5 to-[#FFB703]/5 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#FFB703] text-sm font-medium uppercase tracking-wider mb-4 block">
            {t.pricing.label}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold mb-4">
            {t.pricing.title}
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            {t.pricing.subtitle}
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-stretch mb-12">
          {/* Starter Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0 }}
            className="relative rounded-3xl p-6 md:p-8 glass pricing-card-hover flex flex-col h-full"
            data-testid="pricing-card-starter"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-white/5 border border-white/10">
              <Zap className="w-6 h-6 text-[#FFB703]" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-1">{t.pricing.starter}</h3>
            <p className="text-white/50 text-sm mb-6">{t.pricing.starterDesc}</p>
            <div className="mb-6">
              <span className="text-white/50 text-lg">₹</span>
              <span className="text-4xl md:text-5xl font-semibold text-white">500</span>
              <span className="text-white/50 text-sm">{t.pricing.perListing}</span>
            </div>
            <ul className="space-y-3 mb-8 flex-grow">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.upTo15}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.aiEnhanced}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.basicSeo}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.delivery72}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.whatsappSupport}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.noImages}</span>
              </li>
            </ul>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center py-4 rounded-full font-medium min-h-[52px] bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
              data-testid="pricing-cta-starter"
            >
              {t.pricing.contactNow}
            </a>
          </motion.div>

          {/* Premium Plan - Highlighted */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative rounded-3xl p-6 md:p-8 bg-gradient-to-b from-[#1a1410] to-[#0d0a07] border-2 border-[#F59E0B]/50 md:scale-105 glow-orange flex flex-col h-full"
            data-testid="pricing-card-premium"
          >
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#F59E0B] to-[#FFB703] px-4 py-1.5 rounded-full">
              <span className="text-xs font-semibold text-[#0d0a07] whitespace-nowrap">{t.pricing.premiumBadge}</span>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-gradient-to-br from-[#F59E0B] to-[#FFB703]">
              <Star className="w-6 h-6 text-[#0d0a07]" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-1">{t.pricing.premium}</h3>
            <p className="text-white/50 text-sm mb-6">{t.pricing.premiumDesc}</p>
            <div className="mb-6">
              <span className="text-white/50 text-lg">₹</span>
              <span className="text-4xl md:text-5xl font-semibold text-white">1,000</span>
              <span className="text-white/50 text-sm">{t.pricing.perListing}</span>
            </div>
            <ul className="space-y-3 mb-8 flex-grow">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.upTo40}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.premiumEnhanced}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.advancedSeo}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.delivery48}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.whatsappSupport}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.noImages}</span>
              </li>
            </ul>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center py-4 rounded-full font-medium min-h-[52px] btn-primary"
              data-testid="pricing-cta-premium"
            >
              {t.pricing.contactNow}
            </a>
          </motion.div>

          {/* Custom Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative rounded-3xl p-6 md:p-8 glass pricing-card-hover flex flex-col h-full"
            data-testid="pricing-card-custom"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-white/5 border border-white/10">
              <Crown className="w-6 h-6 text-[#FFB703]" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-1">{t.pricing.custom}</h3>
            <p className="text-white/50 text-sm mb-6">{t.pricing.customDesc}</p>
            <div className="mb-6">
              <span className="text-white/50 text-lg">₹</span>
              <span className="text-4xl md:text-5xl font-semibold text-white">35</span>
              <span className="text-white/50 text-sm">{t.pricing.perItem}</span>
            </div>
            <ul className="space-y-3 mb-8 flex-grow">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.customImages}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.premiumEnhanced}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.advancedSeo}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.delivery48}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.whatsappSupport}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#10B981]">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-white/70">{t.pricing.noImages}</span>
              </li>
            </ul>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center py-4 rounded-full font-medium min-h-[52px] bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
              data-testid="pricing-cta-custom"
            >
              {t.pricing.contactNow}
            </a>
          </motion.div>
        </div>

        {/* Add-ons Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="max-w-2xl mx-auto"
        >
          <div className="glass rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#FFB703] flex items-center justify-center">
                <Plus className="w-5 h-5 text-[#0d0a07]" />
              </div>
              <h3 className="text-xl font-semibold text-white">{t.pricing.addons}</h3>
              <span className="text-white/50 text-sm">{t.pricing.addonsNote}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                <span className="text-white/80 text-sm">{t.pricing.zomatoVideo}</span>
                <span className="text-[#FFB703] font-semibold">₹299</span>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                <span className="text-white/80 text-sm">{t.pricing.zomatoImage}</span>
                <span className="text-[#FFB703] font-semibold">₹99</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
